/**
 * `MailWaitingCue` — the waiting indicator for the agent EMAIL surface.
 *
 * WHY NOT `ThinkingCue`: that one cycles whimsical verbs ("Pondering",
 * "Ideating") because an LLM wait is genuinely open-ended and the churn reads
 * as "actively thinking". A mailbox fetch is a different wait. Measured on
 * galvão (2026-07-22): a warm list is ~5 s, a cold one 30 s+, and the first
 * read of a folder after a container roll can exceed a minute — because the
 * runtime shells out to the himalaya CLI and every invocation opens a FRESH
 * IMAP session (see the perf handoff). At 8 s the lawyer's question is not
 * "what is it thinking about", it is **"is this broken?"**.
 *
 * So this cue answers that question instead of decorating it: the message
 * ESCALATES with elapsed time, ending on an explanation of why a first load is
 * slow. Honest progressive disclosure beats a livelier spinner — a random verb
 * at 40 s actively misleads, because it implies progress we cannot observe.
 *
 * The visual language (breathing mark + shimmer band) is deliberately shared
 * with `ThinkingCue` so "Lexiun is working" looks the same everywhere; only the
 * words differ.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LexiunSpinner } from "@/components/home/LexiunSpinner";
import { cn } from "@/lib/utils";

/** Elapsed-time bands, in ms. Chosen from the measured latency profile. */
export const MAIL_WAIT_STAGES = [
  /** Under this, a normal warm fetch — say nothing beyond "loading". */
  { atMs: 0, key: "agent.channels.email.waiting.loading" },
  /** Past a warm fetch: name the actual work, so the delay has a cause. */
  { atMs: 4000, key: "agent.channels.email.waiting.connecting" },
  /** Past any healthy fetch: explain, so it does not read as a hang. */
  { atMs: 12000, key: "agent.channels.email.waiting.firstRead" },
] as const;

/**
 * Which stage a given elapsed time falls in. Pure + exported so the escalation
 * is unit-testable without faking timers in a component.
 */
export function mailWaitStageKey(elapsedMs: number): string {
  let key: string = MAIL_WAIT_STAGES[0].key;
  for (const s of MAIL_WAIT_STAGES) {
    if (elapsedMs >= s.atMs) key = s.key;
  }
  return key;
}

export function MailWaitingCue({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = Date.now();
    // 1s tick: fine enough to cross a band promptly, coarse enough to be free.
    const id = setInterval(() => setElapsed(Date.now() - started), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="mail-waiting-cue"
      data-stage={mailWaitStageKey(elapsed)}
      role="status"
      // One stable label: the visible text changes as the wait grows, but a
      // screen reader should not have it re-announced on every escalation.
      aria-label={t("agent.channels.email.waiting.loading")}
      className={cn("flex select-none items-center gap-2 text-caption text-cream-500", className)}
    >
      <LexiunSpinner
        variant="breathe"
        aria-hidden
        className="h-4 w-4 shrink-0 text-coffee-700 dark:text-gold-400"
      />
      <span aria-hidden className="animate-text-shimmer">
        {t(mailWaitStageKey(elapsed))}
      </span>
    </div>
  );
}

/**
 * Skeleton rows for the message list.
 *
 * Preferred over a centred spinner because it keeps the column's LAYOUT stable:
 * the list does not jump when real rows arrive, and the shape itself tells the
 * lawyer what is coming. Row geometry mirrors `EmailMessageList`.
 */
export function MailListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div data-testid="mail-list-skeleton" aria-hidden className="flex flex-col">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="flex flex-col gap-1.5 border-b border-cream-200/40 px-3 py-2 dark:border-coffee-800/40"
          // Stagger the pulse so the column reads as one breathing surface
          // rather than eight synchronised blocks.
          style={{ animationDelay: `${i * 90}ms` }}
        >
          <div className="flex items-baseline justify-between gap-2">
            <div className="h-2.5 w-40 animate-pulse rounded bg-cream-200/70 dark:bg-coffee-800/70" />
            <div className="h-2 w-20 animate-pulse rounded bg-cream-200/50 dark:bg-coffee-800/50" />
          </div>
          <div
            className="h-2.5 animate-pulse rounded bg-cream-200/50 dark:bg-coffee-800/50"
            // Vary the subject width so it looks like text, not a table.
            style={{ width: `${58 + ((i * 37) % 34)}%` }}
          />
        </div>
      ))}
    </div>
  );
}
